import type { OrgLocationEntity, ProjectLocationEntity } from '../../domain/entities/OrgLocationEntity.js';
import type { IOrgProjectLocationRepository, LocationExportEntity, LocationExportOptions, LocationImportRow, LocationImportResult, OrgSubscribeInput, ProjectLocationImportPlan, ProjectSubscribeInput, ScheduleImportRow, ScheduleImportResult } from '../../domain/repositories/IOrgProjectLocationRepository.js';
import { OrgProjectLocationDataSource } from '../datasources/OrgProjectLocationDataSource.js';
import type { OrgLocationDto, ProjectLocationDto } from '../dtos/OrgLocationDto.js';

function mapOrgDto(dto: OrgLocationDto): OrgLocationEntity {
  return {
    id: dto.id,
    orgId: dto.org_id,
    locationId: dto.location_id,
    customCode: dto.custom_code ?? null,
    customCategory: dto.custom_category ?? null,
    metadata: dto.metadata ?? {},
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    createdBy: dto.created_by,
  };
}

function mapProjectDto(dto: ProjectLocationDto): ProjectLocationEntity {
  return {
    id: dto.id,
    orgId: dto.org_id,
    projectId: dto.project_id,
    locationId: dto.location_id,
    customCode: dto.custom_code ?? null,
    customCategory: dto.custom_category ?? null,
    workingDays: dto.working_days ?? [],
    geoFenceRadiusM: dto.geo_fence_radius_m ?? 100,
    notes: dto.notes ?? null,
    metadata: dto.metadata ?? {},
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    createdBy: dto.created_by,
  };
}

export class OrgProjectLocationRepository implements IOrgProjectLocationRepository {
  constructor(private readonly ds: OrgProjectLocationDataSource) {}

  async listByOrg(orgId: string): Promise<OrgLocationEntity[]> {
    return (await this.ds.listByOrg(orgId)).map(mapOrgDto);
  }

  async getByOrgLocation(orgId: string, locationId: string): Promise<OrgLocationEntity | null> {
    const dto = await this.ds.getByOrgLocation(orgId, locationId);
    return dto ? mapOrgDto(dto) : null;
  }

  async listByProject(projectId: string): Promise<ProjectLocationEntity[]> {
    return (await this.ds.listByProject(projectId)).map(mapProjectDto);
  }

  async getByProjectLocation(projectId: string, locationId: string): Promise<ProjectLocationEntity | null> {
    const dto = await this.ds.getByProjectLocation(projectId, locationId);
    return dto ? mapProjectDto(dto) : null;
  }

  async hasProjectSubscriptions(orgId: string, locationId: string): Promise<boolean> {
    return this.ds.hasProjectSubscriptions(orgId, locationId);
  }

  async ensureOrgSubscription(orgId: string, locationId: string): Promise<void> {
    await this.ds.ensureOrgSubscription(orgId, locationId);
  }

  async subscribeOrg(input: OrgSubscribeInput): Promise<OrgLocationEntity> {
    const dto = await this.ds.subscribeOrg({
      org_id: input.orgId,
      location_id: input.locationId,
      custom_code: input.customCode ?? null,
      custom_category: input.customCategory ?? null,
    });
    return mapOrgDto(dto);
  }

  async updateOrgLocation(orgId: string, locationId: string, patch: { customCode?: string | null }): Promise<OrgLocationEntity> {
    return mapOrgDto(await this.ds.updateOrgLocation(orgId, locationId, patch));
  }

  async unsubscribeOrg(orgId: string, locationId: string): Promise<void> {
    await this.ds.unsubscribeOrg(orgId, locationId);
  }

  async subscribeProject(input: ProjectSubscribeInput): Promise<ProjectLocationEntity> {
    const dto = await this.ds.subscribeProject({
      org_id: input.orgId,
      project_id: input.projectId,
      location_id: input.locationId,
      custom_code: input.customCode ?? null,
      geo_fence_radius_m: input.geoFenceRadiusM,
      metadata: input.metadata,
    });
    return mapProjectDto(dto);
  }

  async updateProjectLocation(projectId: string, locationId: string, patch: { customCode?: string | null }): Promise<ProjectLocationEntity> {
    return mapProjectDto(await this.ds.updateProjectLocation(projectId, locationId, patch));
  }

  async unsubscribeProject(projectId: string, locationId: string): Promise<void> {
    await this.ds.unsubscribeProject(projectId, locationId);
  }

  async exportByProject(options: LocationExportOptions): Promise<LocationExportEntity[]> {
    const rows = await this.ds.exportByProject(options);
    return rows.map((row) => ({
      projectLocationId: row.id,
      locationId: row.location_id,
      projectId: row.project_id,
      customCode: row.custom_code ?? null,
      locationName: row.location?.name ?? '',
      locationCode: row.location?.code ?? '',
      addressLine: row.location?.address_line ?? null,
      fullAddressCache: row.location?.full_address_cache ?? null,
      provinceCode: row.location?.province_code ?? null,
      latitude: row.location?.latitude ?? null,
      longitude: row.location?.longitude ?? null,
      createdAt: row.created_at,
    }));
  }

  async listByProjectCustomCodes(projectId: string, codes: string[]): Promise<Map<string, string>> {
    const rows = await this.ds.listByProjectCustomCodes(projectId, codes);
    return new Map(rows.map((r) => [r.custom_code, r.location_id]));
  }

  async importProjectSchedule(orgId: string, projectId: string, rows: ScheduleImportRow[]): Promise<ScheduleImportResult[]> {
    const payload = rows.map((r) => ({
      key: r.key,
      session_id: r.sessionId ?? null,
      location_id: r.locationId ?? null,
      code: r.code ?? null,
      custom_code: r.customCode ?? null,
      achiever_id: r.achieverId ?? null,
      scheduled_start_at: r.scheduledStartAt ?? null,
      scheduled_end_at: r.scheduledEndAt ?? null,
      started_at: r.startedAt ?? null,
      ended_at: r.endedAt ?? null,
      notes: r.notes ?? null,
    }));
    const result = await this.ds.importProjectSchedule(orgId, projectId, payload);
    return result.map((r) => ({
      key: r.key,
      ok: r.ok,
      locationId: r.location_id,
      sessionId: r.session_id,
      error: r.error,
    }));
  }

  async previewProjectLocationImport(orgId: string, projectId: string, rows: Array<Record<string, unknown>>): Promise<ProjectLocationImportPlan> {
    return this.ds.previewProjectLocationImport(orgId, projectId, rows);
  }

  async commitQueuedProjectLocationImport(orgId: string, projectId: string, importId: string): Promise<void> {
    await this.ds.commitQueuedProjectLocationImport(orgId, projectId, importId);
  }

  async queueProjectLocationImport(orgId: string, projectId: string, rows: LocationImportRow[]): Promise<{ importId: string }> {
    return this.ds.queueProjectLocationImport(orgId, projectId, rows);
  }

  async getQueuedProjectLocationImportStatus(orgId: string, projectId: string, importId: string): Promise<Record<string, unknown>> {
    return this.ds.getQueuedProjectLocationImportStatus(orgId, projectId, importId);
  }

  async resumeQueuedProjectLocationImport(orgId: string, projectId: string, importId: string): Promise<void> {
    await this.ds.resumeQueuedProjectLocationImport(orgId, projectId, importId);
  }

  async confirmQueuedProjectLocationImportDuplicates(orgId: string, projectId: string, importId: string, rowNumbers: number[]): Promise<void> {
    await this.ds.confirmQueuedProjectLocationImportDuplicates(orgId, projectId, importId, rowNumbers);
  }

  async useQueuedProjectLocationImportDuplicates(orgId: string, projectId: string, importId: string, rowNumbers: number[]): Promise<{ alreadySubscribed: number }> {
    return this.ds.useQueuedProjectLocationImportDuplicates(orgId, projectId, importId, rowNumbers);
  }

  async importProjectLocations(orgId: string, projectId: string, rows: LocationImportRow[]): Promise<LocationImportResult[]> {
    const payload = rows.map((r) => ({
      key: r.key,
      action: r.action,
      location_id: r.locationId ?? null,
      code: r.code ?? null,
      custom_code: r.customCode ?? null,
      name: r.name ?? null,
      address_line: r.addressLine ?? null,
      full_address_cache: r.fullAddressCache ?? null,
      province_name: r.provinceName ?? null,
      categories: r.categories ?? [],
      geo_fence_radius_m: r.geoFenceRadiusM ?? null,
      location_metadata: r.locationMetadata ?? {},
    }));
    const result = await this.ds.importProjectLocations(orgId, projectId, payload);
    return result.map((r) => ({
      key: r.key,
      ok: r.ok,
      action: r.action as LocationImportResult['action'],
      locationId: r.location_id,
      error: r.error,
    }));
  }
}
